import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  LayoutGrid, Users, Package, DollarSign, FileText,
  BarChart3, TrendingUp, UserPlus, Settings, LogOut,
  Zap, Moon, Sun, Bell, Menu, Search, ChevronRight, Plus,
  Handshake, Network
} from 'lucide-react';

const navGroups = [
  {
    label: 'Vertrieb',
    items: [
      { title: 'Dashboard', icon: LayoutGrid, url: '/app', exact: true },
      { title: 'Leads', icon: Users, url: '/app/leads' },
      { title: 'Angebote', icon: FileText, url: '/app/angebote' },
      { title: 'Abschluesse', icon: Handshake, url: '/app/abschluesse' },
    ]
  },
  {
    label: 'Energie',
    items: [
      { title: 'Kalkulator', icon: Zap, url: '/app/kalkulator' },
      { title: 'Versorger', icon: Package, url: '/app/versorger' },
      { title: 'Preise', icon: DollarSign, url: '/app/preise/eingabe' },
      { title: 'Netzentgelte', icon: Network, url: '/app/netzentgelte' },
      { title: 'Lastgang-Analyse', icon: BarChart3, url: '/app/lastgang' },
      { title: 'Marktdaten', icon: TrendingUp, url: '/app/markt' },
    ]
  },
];

const adminItems = [
  { title: 'Team', icon: UserPlus, url: '/app/team' },
  { title: 'Einstellungen', icon: Settings, url: '/app/einstellungen' },
];

const InternalLayout = () => {
  const { profile, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const toggleDark = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle('dark', next);
  };

  const initials = profile?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'AQ';

  const renderNavItem = (item) => {
    const isActive = item.exact
      ? location.pathname === item.url
      : location.pathname.startsWith(item.url);
    return (
      <NavLink
        key={item.url}
        to={item.url}
        onClick={() => setSidebarOpen(false)}
        className={cn('hk-navlink', isActive && 'active')}
      >
        <item.icon className="icon" />
        <span>{item.title}</span>
      </NavLink>
    );
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-[260px] bg-sidebar border-r border-sidebar-border flex flex-col transition-transform duration-200 ease-in-out",
        "lg:relative lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Brand */}
        <div className="h-[70px] flex items-center px-6 border-b border-sidebar-border">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
              <Zap className="text-primary-foreground" size={18} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-bold text-base text-sidebar-foreground leading-none">AIQCON</div>
              <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mt-0.5">Energy Broker</div>
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4">
          {navGroups.map(group => (
            <div key={group.label} className="mb-2">
              <div className="nav-group-label">{group.label}</div>
              <nav className="space-y-0.5">
                {group.items.map(renderNavItem)}
              </nav>
            </div>
          ))}

          {isAdmin && (
            <div className="mb-2">
              <div className="nav-group-label">System</div>
              <nav className="space-y-0.5">
                {adminItems.map(renderNavItem)}
              </nav>
            </div>
          )}
        </div>

        {/* User profile */}
        <div className="border-t border-sidebar-border p-4">
          <div className="flex items-center gap-3">
            <div className="hk-avatar size-md">{initials}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-sidebar-foreground truncate">{profile?.full_name || 'Demo User'}</p>
              <p className="text-xs text-muted-foreground truncate capitalize">{profile?.role || 'admin'}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
              title="Abmelden"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 h-[70px] bg-card border-b border-border flex items-center px-4 lg:px-6 gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent"
          >
            <Menu size={20} />
          </button>

          {/* Search */}
          <div className="hidden md:flex items-center gap-2 bg-background/60 border border-border rounded-lg px-3 py-2 w-full max-w-md">
            <Search size={16} className="text-muted-foreground" />
            <input
              placeholder="Suche nach Leads, Angeboten, Versorgern..."
              className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground"
            />
            <kbd className="hidden lg:inline-block text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded border">⌘K</kbd>
          </div>

          <div className="flex-1" />

          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={toggleDark}
              className="w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              title={darkMode ? 'Light Mode' : 'Dark Mode'}
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button className="w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors relative">
              <Bell size={18} />
              <span className="absolute top-2 right-2 w-2 h-2 bg-destructive rounded-full ring-2 ring-card" />
            </button>
            <div className="w-px h-8 bg-border mx-1" />
            <button
              onClick={() => navigate('/app/leads')}
              className="hidden sm:flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
            >
              <Plus size={16} />
              <span>Neuer Lead</span>
            </button>
            <div className="hk-avatar size-sm ml-2">{initials}</div>
          </div>
        </header>

        {/* Content */}
        <main className="dashboard-body flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default InternalLayout;
